import React from 'react';
import {
  ExternalLink,
  FileText,
  GitBranch,
  LayoutGrid,
  Briefcase,
  Layers,
  Download,
} from 'lucide-react';
import ModernLayout from './ModernLayout';
import './UnstructAIPage.css';

const UNSTRUCT_AI_URL = 'https://unstruct-ai-frontend.onrender.com/';

const capabilities = [
  {
    icon: FileText,
    title: 'PDF table detection',
    description:
      'Upload financial PDFs and automatically detect income statements, balance sheets, and other tabular data using AI.',
  },
  {
    icon: GitBranch,
    title: 'Smart mapping',
    description:
      'Map extracted line items to your custom financial template. AI matches terms and handles aliases for consistency.',
  },
  {
    icon: LayoutGrid,
    title: 'Master data view',
    description:
      'Consolidate multiple periods into a single master spreadsheet. Export to CSV or Excel for reporting and analysis.',
  },
  {
    icon: Briefcase,
    title: 'Entity-based workflow',
    description:
      'Organize work by entity (bank or organization). Add periods, track extractions, and update master data with one click.',
  },
  {
    icon: Layers,
    title: 'Multi-page tables',
    description:
      'Handles tables that span multiple pages. Select which tables to extract and configure columns for each.',
  },
  {
    icon: Download,
    title: 'Export anywhere',
    description:
      'Export master data to CSV or Excel. Use in your reporting tools, BI dashboards, or for further analysis.',
  },
];

const UnstructAIPage = () => {
  const handleOpenUnstructAI = () => {
    window.open(UNSTRUCT_AI_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <ModernLayout showTwoPanel={false}>
      <div className="unstruct-ai-page">
        <div className="unstruct-ai-hero">
          <h1 className="unstruct-ai-title">
            Extract financial metrics from unstructured data in minutes
          </h1>
          <p className="unstruct-ai-subtitle">
            AI-powered document analysis that identifies tables, extracts data,
            and maps it to your financial template. No more manual copying from
            quarterly reports.
          </p>
          <button
            type="button"
            className="unstruct-ai-cta unstruct-ai-cta-hero"
            onClick={handleOpenUnstructAI}
          >
            <ExternalLink size={18} />
            <span>Open Unstruct AI</span>
          </button>
        </div>

        <section className="unstruct-ai-about">
          <h2>About Unstruct AI</h2>
          <p>
            Unstruct AI helps you consolidate financial data from multiple
            documents into a single, structured format. It detects tables in
            PDFs, maps line items to your template, and lets you export to CSV
            or Excel—reducing manual work and errors. Open the app below to get
            started.
          </p>
        </section>

        <section className="unstruct-ai-capabilities">
          <h2>Capabilities</h2>
          <p className="unstruct-ai-capabilities-intro">
            Everything you need to consolidate financial data from multiple
            documents into one structured format.
          </p>
          <div className="unstruct-ai-capability-grid">
            {capabilities.map(({ icon: Icon, title, description }) => (
              <div key={title} className="unstruct-ai-capability-card">
                <div className="unstruct-ai-capability-icon">
                  <Icon size={20} />
                </div>
                <h3>{title}</h3>
                <p>{description}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="unstruct-ai-footer-cta">
          <button
            type="button"
            className="unstruct-ai-cta"
            onClick={handleOpenUnstructAI}
          >
            <ExternalLink size={18} />
            <span>Open Unstruct AI</span>
          </button>
        </div>
      </div>
    </ModernLayout>
  );
};

export default UnstructAIPage;
